import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, ArrowPathIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import logger from '../utils/logger';

function UserManagementView({ token, setError }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null); // Track which user's email is being sent
  const [editingRole, setEditingRole] = useState(null); // Track which user's role is being edited
  const [newRole, setNewRole] = useState(''); // Store the new role value
  const [viewingSummary, setViewingSummary] = useState(null); // Track which user's summary is being viewed
  const [regeneratingSummary, setRegeneratingSummary] = useState(null); // Track which user's summary is being regenerated

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users.');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      logger.error('Error in UserManagementView:', err);
      setError(err.message || 'An error occurred while fetching users.');
      setUsers([]); // Clear users on error
    } finally {
      setLoading(false);
    }
  };

  // --- Email Sending Placeholder ---
  // This needs a corresponding backend endpoint (e.g., POST /api/admin/users/:userId/send-email)
  const handleSendEmail = async (userId, emailType = 'welcome') => {
    if (sendingEmail) return; // Prevent multiple clicks
    if (!window.confirm(`Send ${emailType} email to this user?`)) return;

    setSendingEmail(userId); // Indicate sending started for this user
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailType }), // Send type in body
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to send ${emailType} email.`);
      }

      // Optionally show a success message (e.g., using a toast notification library)
      // Use the message from the backend simulation for now
      const result = await response.json();
      alert(result.message || `${emailType.charAt(0).toUpperCase() + emailType.slice(1)} email sent successfully!`);


    } catch (err) {
      logger.error('Error in UserManagementView:', err);
      setError(err.message || `An error occurred while sending the ${emailType} email.`);
    } finally {
      setSendingEmail(null); // Reset sending state
    }
  };
  // --- End Email Sending Placeholder ---

  // --- Role Editing ---
  const handleStartEditRole = (user) => {
    setEditingRole(user._id);
    setNewRole(user.role);
  };

  const handleCancelEditRole = () => {
    setEditingRole(null);
    setNewRole('');
  };

  const handleSaveRole = async (userId) => {
    if (!newRole) {
      setError('Role cannot be empty.');
      return;
    }

    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user role.');
      }

      const updatedUser = await response.json();
      setUsers(users.map(u => u._id === userId ? updatedUser : u));
      setEditingRole(null);
      setNewRole('');
    } catch (err) {
      logger.error('Error in UserManagementView:', err);
      setError(err.message || 'An error occurred while updating the user role.');
    }
  };

  // Quick role set for dev testing (uses dev endpoint)
  const handleQuickSetRole = async (userId, role) => {
    if (!window.confirm(`Set user role to "${role}"? (Dev mode only)`)) return;

    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/dev/set-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, role }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set user role.');
      }

      const result = await response.json();
      setUsers(users.map(u => u._id === userId ? result.user : u));
      alert(result.message);
    } catch (err) {
      logger.error('Error in UserManagementView:', err);
      setError(err.message || 'An error occurred while setting the user role.');
    }
  };
  // --- End Role Editing ---

  // --- AI Summary Management ---
  const handleViewSummary = async (userId) => {
    // If already viewing this user's summary, close it
    if (viewingSummary === userId) {
      setViewingSummary(null);
      return;
    }

    // Fetch the latest user data to get the current summary
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // Preserve completedChapters count when updating user data
        const existingUser = users.find(u => u._id === userId);
        setUsers(users.map(u => 
          u._id === userId 
            ? { ...userData, completedChapters: existingUser?.completedChapters ?? 0 }
            : u
        ));
        setViewingSummary(userId);
      } else {
        setError('Failed to fetch user summary.');
      }
    } catch (err) {
      logger.error('Error fetching user summary:', err);
      setError('An error occurred while fetching the user summary.');
    }
  };

  const handleRegenerateSummary = async (userId) => {
    if (!window.confirm('Regenerate AI summary for this user? This will use OpenAI to create a new summary based on their feedback.')) {
      return;
    }

    setRegeneratingSummary(userId);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}/regenerate-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to regenerate AI summary.');
      }

      const result = await response.json();
      
      // Update the user in the list with the new summary, preserving completedChapters
      const existingUser = users.find(u => u._id === userId);
      setUsers(users.map(u => 
        u._id === userId 
          ? { ...u, aiSummary: result.summary, completedChapters: existingUser?.completedChapters ?? 0 }
          : u
      ));

      // Show the updated summary
      setViewingSummary(userId);
      
      alert(result.message || 'AI summary regenerated successfully!');
    } catch (err) {
      logger.error('Error regenerating AI summary:', err);
      setError(err.message || 'An error occurred while regenerating the AI summary.');
    } finally {
      setRegeneratingSummary(null);
    }
  };
  // --- End AI Summary Management ---

  const filteredUsers = users.filter(user =>
    (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.role?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg dark:bg-gray-700">

      {/* Search Bar */}
      <div className="mb-4 dark:text-white dark:border-gray-600">
        <input
          type="text"
          placeholder="Search by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        />
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-4 dark:bg-gray-800 dark:text-gray-400">Loading users...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-800">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-800">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-800">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-800">
                  Progress
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-800">
                  AI Summary
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 dark:bg-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{user.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-white">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-white">
                      {editingRole === user._id ? (
                        <div className="flex items-center space-x-2">
                          <select
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            autoFocus
                          >
                            <option value="student">student</option>
                            <option value="pro">pro</option>
                            <option value="admin">admin</option>
                          </select>
                          <button
                            onClick={() => handleSaveRole(user._id)}
                            className="text-green-600 hover:text-green-800 text-xs px-2 py-1 rounded hover:bg-green-50"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEditRole}
                            className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.role === 'admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'pro' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800' // Default for 'student' or others
                          }`}>
                            {user.role}
                          </span>
                          <button
                            onClick={() => handleStartEditRole(user)}
                            className="text-gray-500 hover:text-gray-700 rounded text-xs px-1 dark:text-white dark:hover:text-gray-300 dark:hover:bg-gray-600 rounded hover:bg-gray-100"
                            title="Edit Role"
                          >
                            ✏️
                          </button>
                          {/* Quick set buttons for dev testing */}
                          {process.env.NODE_ENV === 'development' && (
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleQuickSetRole(user._id, 'student')}
                                className="text-xs px-1 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
                                title="Quick set to student"
                              >
                                S
                              </button>
                              <button
                                onClick={() => handleQuickSetRole(user._id, 'pro')}
                                className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800"
                                title="Quick set to pro"
                              >
                                P
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-200">
                      {/* Display completed chapters count (now a number from backend, not an array) */}
                      {typeof user.completedChapters === 'number' 
                        ? `${user.completedChapters} ${user.completedChapters === 1 ? 'Chapter' : 'Chapters'}` 
                        : '0 Chapters'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-200">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewSummary(user._id)}
                          className="p-1.5 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:text-blue-300 dark:hover:text-blue-400 dark:hover:bg-blue-900"
                          title="View AI Summary"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRegenerateSummary(user._id)}
                          disabled={regeneratingSummary === user._id}
                          className="p-1.5 rounded text-green-600 hover:text-green-800 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-green-300 dark:hover:text-green-400 dark:hover:bg-green-900"
                          title="Regenerate AI Summary"
                        >
                          {regeneratingSummary === user._id ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <ArrowPathIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2 dark:text-white">
                      {/* Example Email Button */}
                      <button
                        onClick={() => handleSendEmail(user._id, 'welcome')}
                        disabled={sendingEmail === user._id}
                        title="Send Welcome Email"
                        className={`p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-blue-300 dark:hover:text-blue-400 dark:hover:bg-blue-900`}
                      >
                        {sendingEmail === user._id ? 'Sending...' : <EnvelopeIcon className="h-5 w-5" />}
                      </button>
                      {/* Add other actions like 'Edit Role', 'View Details', 'Send Reminder Email' etc. */}
                      {/* Example: Send Reminder Email Button */}
                      {/*
                      <button
                        onClick={() => handleSendEmail(user._id, 'reminder')}
                        disabled={sendingEmail === user._id}
                        title="Send Reminder Email"
                        className={`p-1 rounded text-orange-600 hover:text-orange-800 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {sendingEmail === user._id ? 'Sending...' : <BellAlertIcon className="h-5 w-5" />} // Assuming BellAlertIcon is imported
                      </button>
                      */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No users found matching your search criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* AI Summary Modal */}
      {viewingSummary && (() => {
        const user = users.find(u => u._id === viewingSummary);
        if (!user) return null;
        
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
            onClick={(e) => {
              // Close modal when clicking on the backdrop
              if (e.target === e.currentTarget) {
                setViewingSummary(null);
              }
            }}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col m-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    AI Summary for {user.name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{user.email}</p>
                </div>
                <button
                  onClick={() => setViewingSummary(null)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                {user.aiSummary ? (
                  <div className="prose dark:prose-invert max-w-none">
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                        {user.aiSummary}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">
                      No AI summary available yet. Generate one by clicking the regenerate button.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
                <button
                  onClick={() => handleRegenerateSummary(user._id)}
                  disabled={regeneratingSummary === user._id}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {regeneratingSummary === user._id ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      <span>Regenerating...</span>
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4" />
                      <span>Regenerate Summary</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setViewingSummary(null)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default UserManagementView;